
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Play, Vault, Sparkles, Award, Star } from 'lucide-react';
import withInteractiveAnimation from '../animations/withInteractiveAnimation';

interface CardDeckProps {
  className?: string;
}

const CardDeck: React.FC<CardDeckProps> = ({ className }) => {
  const [isShuffling, setIsShuffling] = useState(false);
  
  const handleShuffle = () => {
    if (isShuffling) return;
    
    setIsShuffling(true);
    setTimeout(() => {
      setIsShuffling(false);
    }, 1000);
  };

  return (
    <div 
      className={cn(
        "relative w-full max-w-[300px] h-64 mx-auto my-8 perspective-1000",
        className
      )}
    >
      {/* Aura badge at the top */}
      <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 p-2 rounded-full shadow-lg animate-pulse">
        <Sparkles size={20} className="text-white" />
      </div>
      
      {/* Floating aura sparkles in background */}
      <div className="absolute top-4 left-5 z-10 text-yellow-500 animate-bounce opacity-70">
        <Sparkles size={14} />
      </div>
      <div className="absolute bottom-10 right-10 z-10 text-amber-400 animate-bounce opacity-70" style={{ animationDelay: "0.5s" }}>
        <Sparkles size={14} />
      </div>
      <div className="absolute top-20 right-8 z-10 text-yellow-400 animate-bounce opacity-70" style={{ animationDelay: "1s" }}>
        <Sparkles size={14} />
      </div>
      
      {/* Animated sparkles */}
      <div className="absolute -top-2 -left-2 z-10 text-amber-300 animate-pulse">
        <Star size={16} fill="currentColor" />
      </div>
      <div className="absolute top-10 -right-2 z-10 text-amber-300 animate-pulse" style={{ animationDelay: "0.7s" }}>
        <Star size={16} fill="currentColor" />
      </div>
      <div className="absolute -bottom-2 left-10 z-10 text-amber-300 animate-pulse" style={{ animationDelay: "1.3s" }}>
        <Star size={16} fill="currentColor" />
      </div>
      
      {/* Achievement badge */}
      <div className="absolute -left-5 top-1/3 z-20 rotate-[-25deg] scale-90">
        <Award className="text-purple-500 drop-shadow-[0_0_5px_rgba(168,85,247,0.6)]" size={32} />
      </div>
      
      {/* Card stack effect with multiple cards */}
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className={cn(
            "absolute inset-0 rounded-xl border border-primary/20 bg-gradient-to-br",
            "shadow-lg transition-all duration-500 ease-out",
            isShuffling ? "animate-variable-scale" : "",
            index === 0 && "from-primary/10 to-accent/10 shadow-lg border-yellow-500/40",
            index === 1 && "-translate-y-1 -rotate-2 from-primary/8 to-accent/8",
            index === 2 && "-translate-y-2 rotate-1 from-primary/6 to-accent/6",
            index === 3 && "-translate-y-3 -rotate-1 from-primary/4 to-accent/4",
            index === 4 && "-translate-y-4 rotate-2 from-primary/2 to-accent/2",
          )}
          style={{
            transformOrigin: "center",
            zIndex: 5 - index,
            boxShadow: index === 0 ? "0 0 15px rgba(234, 179, 8, 0.3)" : "", // Golden glow for top card
          }}
        />
      ))}
      
      {/* Elite badge on top card */}
      <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-yellow-600 to-amber-500 px-2 py-0.5 rounded-md text-xs font-bold text-white shadow-md transform rotate-[-5deg] opacity-90">
        ELITE
      </div>
      
      {/* Decorative ribbon */}
      <div className="absolute -right-3 top-3 z-10 w-24 h-6 bg-gradient-to-r from-purple-600 to-primary rotate-[30deg] shadow-md"></div>
      
      {/* Play button */}
      <button
        className={cn(
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10",
          "flex items-center justify-center gap-2 px-4 py-2",
          "bg-primary/80 hover:bg-primary text-white rounded-full",
          "transition-all duration-300 backdrop-blur-sm",
          "shadow-lg hover:shadow-primary/20 hover:scale-105 shadow-[0_0_15px_rgba(155,135,245,0.5)]",
          isShuffling ? "scale-90 opacity-80" : ""
        )}
        onClick={handleShuffle}
      >
        <Play size={16} />
        <span className="font-medium text-sm">Shuffle</span>
      </button>
      
      {/* Vault indicator */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white shadow-md">
        <Vault size={12} className="text-yellow-400" />
        <span className="text-yellow-200">Premium Vault</span>
      </div>
      
      {/* Points badge */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 bg-yellow-600/80 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white shadow-md">
        <Sparkles size={12} className="text-yellow-300" />
        <span>+5000 pts</span>
      </div>
    </div>
  );
};

export default withInteractiveAnimation(CardDeck, 'ripple');
